/**
 * Beta Preview — the employee switch that adds the EXTRA knowledge sources to Copilot (ACOP-140,
 * David, 2026-10-13).
 *
 * Deliberately free of React so the rules can be unit-tested directly — the same reasoning as
 * utils/nav.ts and utils/landing.ts.
 *
 * ── What it is, and what it is NOT ─────────────────────────────────────────────────────────────
 *
 * Home, Copilot and the Knowledge Base are the LIVE experience for everyone. They are not behind
 * this switch and never were again after ACOP-140 — the switch used to swap the old pages for the new
 * ones (2026-08-28) and no longer does. It survives for two reasons:
 *
 *   1. It switches on the other knowledge sources for employee testing — the internal KB, product
 *      knowledge, support cases, change requests and feature requests (utils/sourcePicker.ts). The RAG
 *      cannot yet combine those reliably, so they stay eliminated unless this is on.
 *   2. Other beta features (the LMS being embedded, for one) hang off it.
 *
 * It is a PREFERENCE, not a security boundary: what a user may SEARCH is decided in AVS OS from the
 * employee role (ACOP-142 / OS-188), so flipping this can only ever narrow or widen a request the
 * server will bound anyway. It is visible to EMPLOYEES ONLY and fails CLOSED.
 *
 * ── It is never saved ──────────────────────────────────────────────────────────────────────────
 *
 * It starts OFF on every load and its state is not persisted anywhere (David, 2026-10-13: "dont even
 * save its state"). The old localStorage key is removed on load so a value left by the previous
 * implementation cannot linger.
 */

/** The localStorage key the previous implementation used. Only ever REMOVED now. */
export const LEGACY_BETA_PREVIEW_KEY = "copilotBetaPreview";

/**
 * Whether this caller may use Beta Preview at all: an EMPLOYEE whose engagement is not denied.
 *
 * Fails CLOSED. A missing, malformed or non-array roles value yields false, and so does a denied
 * employee — `determineRoles` gives that caller ["guest", "employee"], and "employee" is not a tier,
 * so the tier check below is what keeps them out (copilot-api would refuse them anyway).
 */
export function canUseBetaPreview(roles: unknown[]): boolean {
  if (!Array.isArray(roles) || !roles.includes("employee")) return false;
  return roles.some((r) => r === "pending" || r === "standard" || r === "support");
}

/**
 * Whether Beta Preview is actually ON for this caller.
 *
 * Takes the in-memory flag. Only a literal `true` counts, and only for a caller who may use it at
 * all, so a stale flag after a role refresh reveals nothing.
 */
export function isBetaPreviewOn(roles: unknown[], on: unknown): boolean {
  return canUseBetaPreview(roles) && on === true;
}

/** Remove the preference the previous implementation saved. Tolerates a browser that refuses storage. */
export function clearLegacyBetaPreview(): void {
  try {
    window.localStorage.removeItem(LEGACY_BETA_PREVIEW_KEY);
  } catch {
    /* Safari private mode and blocked site data THROW; there is nothing to clean up then. */
  }
}
